import { useEffect } from "react";
import { useSections } from "@/api/cms";
import { useCatalogFlowPreview } from "@/context/cms-puck/CatalogFlowPreviewContext";
import { useAuth } from "@/context/auth/AuthContext";
import { useCmsNew } from "@/lib/cms/cms-new-i18n";
import { getMainLanguage } from "@/i18n";
import {
  PuckSafeSelect as Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/cms-puck/fields/PuckSafeSelect";

function sectionLabel(
  section: {
    id: string;
    nameMultilingual?: Record<string, string> | null;
  },
  lang: string,
): string {
  const names = section.nameMultilingual ?? {};
  return (
    names[lang] ||
    Object.values(names).find((v) => typeof v === "string" && v.trim()) ||
    section.id
  );
}

/**
 * Sélecteur de découpage d'aperçu (éditeur) pour `CatalogueListing`.
 * Cible héritée du parcours catalogue ; sur le site, l'URL prime.
 */
export function CatalogueListingPreviewDecoupageField({
  value,
  onChange,
}: {
  value: string | null | undefined;
  onChange: (next: string | null) => void;
}) {
  const cmsNew = useCmsNew();
  const { instanceData } = useAuth();
  const cmsId = instanceData?.CMS?.id ?? "";
  const { target, isLoading: flowLoading } = useCatalogFlowPreview();
  const listTarget = target === "MULTIPRINTS" ? "MULTIPRINTS" : "ARTWORKS";
  const mainLang = getMainLanguage();

  const { data: sections = [], isLoading } = useSections(cmsId, listTarget);

  useEffect(() => {
    if (sections.length === 0) return;
    const stillValid = value && sections.some((s) => s.id === value);
    if (stillValid) return;
    onChange(sections[0]?.id ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ancrer défaut une fois la liste prête
  }, [sections.length, listTarget]);

  if (flowLoading || isLoading) {
    return (
      <p className="p-1 text-xs text-slate-400">
        {cmsNew("cms.new.auto.text.chargement", "Chargement…")}
      </p>
    );
  }

  if (sections.length === 0) {
    return (
      <p className="p-1 text-xs text-slate-500">
        {cmsNew(
          "cms.new.hint.catalogueListingPreview.empty",
          "Aucun découpage pour cette cible.",
        )}
      </p>
    );
  }

  const selectedId =
    value && sections.some((s) => s.id === value)
      ? value
      : (sections[0]?.id ?? "");

  return (
    <div className="space-y-1.5 p-1">
      <Select
        value={selectedId}
        onValueChange={(id) => onChange(id || null)}
      >
        <SelectTrigger className="h-10 w-full bg-white">
          <SelectValue
            placeholder={cmsNew(
              "cms.new.auto.placeholder.choisir_un_decoupage",
              "Choisir un découpage…",
            )}
          />
        </SelectTrigger>
        <SelectContent className="max-h-72">
          {sections.map((s) => (
            <SelectItem key={s.id} value={s.id}>
              {sectionLabel(s, mainLang)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
